import { useAppStore } from "@/stores";
import {
  Card,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Bar,
  BarChart,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
  Legend,
  Cell,
  ReferenceLine,
} from "recharts";
import {
  combineEarnedAndProjectedCommissionMonths,
  filterCurrentMonthOnwards,
  getAggregateEarned,
  getCurrencyFormatted,
  getCurrencySymbol,
  getMonthsList,
} from "@/lib/app";
import {
  CommissionMonth,
  CommissionMonthWithProjected,
  Database,
  NestedCommission,
  TODO,
} from "@revelate/types";

type CommissionMonthGraphAndTooltip = CommissionMonthWithProjected & {
  earned: number;
  projectionDifference: number;
};

function getCommissionsByMonthForGraphAndTooltip(
  commissionsByMonth: CommissionMonthWithProjected[]
): CommissionMonthGraphAndTooltip[] {
  return commissionsByMonth.map((cm) => {
    const earned = cm.value;

    // Don't show "negative" projections
    let projectionDifference =
      cm.projectedValue >= earned ? cm.projectedValue - earned : 0;

    return {
      ...cm,
      earned,
      projectionDifference,
    };
  });
}

const CustomTooltip = ({ active, payload, label }: TODO) => {
  const { scope, companyCurrency } = useAppStore((state) => state);

  if (active && payload && payload.length) {
    // Values
    const earned = payload?.[0]?.payload?.value ?? 0;
    const projected = payload?.[0]?.payload?.projectedValue ?? 0;
    // const projectionDifference =
    //   payload?.[0]?.payload?.projectionDifference ?? 0;

    // Currency formatted values
    const earnedFormatted = getCurrencyFormatted(earned, companyCurrency?.code);
    const projectedOnlyFormatted = getCurrencyFormatted(
      projected,
      companyCurrency?.code
    );
    // const projectionDifferenceFormatted = getCurrencyFormatted(
    //   projectionDifference,
    //   companyCurrency?.code
    // );

    // Labels
    const earnedLine = `${earnedFormatted} earned`;
    const projectedLine = `${projectedOnlyFormatted} forecasted`;
    // const projectionDifferenceLine = `${projectionDifferenceFormatted} forecasted`;

    const scopeLabel = scope?.label ? ` • ${scope.label}` : "";

    // Build tooltip text
    const hasEarned = earned > 0;
    const hasProjected = projected > 0;

    // const hasProjectedBelowEarned = projected > 0 && projected <= earned;
    // const hasProjectionAboveEarned = projectionDifference > 0;

    // Build summary
    let textLines = [];

    // Earned first
    if (hasEarned) {
      textLines.push(earnedLine);
    }

    // Then projected
    if (hasProjected) {
      if (textLines.length > 0) textLines.push(`+ ${projectedLine}`);
      else textLines.push(projectedLine);
    }

    // Then only projection if below earned
    // But only difference if above earned
    // if (hasProjectionBelowEarned && !hasProjectionAboveEarned) {
    //   textLines.push(`+ ${projectedLine}`);
    // } else if (hasProjectionAboveEarned) {
    //   textLines.push(`+ ${projectionDifferenceLine}`);
    // }

    return (
      <Card className="max-w-64" key={label}>
        <CardHeader>
          <CardTitle className="text-sm font-semibold">
            {label}
            {scopeLabel}
          </CardTitle>
          <div className="flex flex-col gap-y-0">
            {textLines.map(
              (line, index) =>
                line !== "" && (
                  <CardDescription key={index} className="text-sm">
                    {line}
                  </CardDescription>
                )
            )}
          </div>
        </CardHeader>
      </Card>
    );
  }

  return null;
};

function formatAxisTickValue(
  value: number,
  companyCurrency: Database["public"]["Tables"]["currencies"]["Row"] | null
) {
  const formattedValue =
    value >= 1_000_000
      ? `${value / 1_000_000}M`
      : value >= 1_000
        ? `${value / 1_000}k`
        : value.toString();

  return `${formattedValue} ${getCurrencySymbol(companyCurrency?.code)}`;
}

interface OverviewBarChartProps {
  earnedCommissions: NestedCommission[];
  projectedCommissions?: NestedCommission[];
}

export function OverviewBarChart({
  earnedCommissions,
  projectedCommissions,
}: OverviewBarChartProps) {
  const { dateRange, companyCurrency } = useAppStore((state) => state);

  let commissionsByMonth: CommissionMonthWithProjected[] = [];

  const earnedCommissionsByMonth: CommissionMonth[] = getMonthsList(
    getAggregateEarned(earnedCommissions),
    dateRange
  );

  if (projectedCommissions && projectedCommissions.length) {
    const projectedCommissionsByMonth: CommissionMonth[] = getMonthsList(
      filterCurrentMonthOnwards(projectedCommissions),
      dateRange
    );
    commissionsByMonth = combineEarnedAndProjectedCommissionMonths(
      earnedCommissionsByMonth,
      projectedCommissionsByMonth
    );
  } else {
    commissionsByMonth = earnedCommissionsByMonth.map((cm) => ({
      ...cm,
      projectedValue: 0,
    }));
  }

  const commissionsByMonthForGraphAndTooltip =
    getCommissionsByMonthForGraphAndTooltip(commissionsByMonth);

  const CustomLegend = () => {
    return (
      <div className="flex flex-row justify-center items-center gap-x-5 mt-1">
        <div className="flex flex-row justify-center items-center gap-x-1.5">
          <div className="w-3 h-3 rounded-full bg-[#3A3361]" />
          <p className="text-gray-500 text-sm">Earned</p>
        </div>
        <div className="flex flex-row justify-center items-center gap-x-1.5">
          <div className="w-3 h-3 rounded-full bg-[#9F86C0]" />
          <p className="text-gray-500 text-sm font-normal">Forecasted</p>
        </div>
      </div>
    );
  };

  return (
    <ResponsiveContainer width="100%" height={350}>
      <BarChart data={commissionsByMonthForGraphAndTooltip} stackOffset="sign">
        <XAxis
          dataKey="label"
          stroke="#888888"
          fontSize={12}
          tickLine={false}
          axisLine={false}
        />
        <YAxis
          stroke="#888888"
          fontSize={12}
          tickLine={false}
          axisLine={false}
          tickFormatter={(value) => formatAxisTickValue(value, companyCurrency)}
        />
        <ReferenceLine y={0} color="black" />
        <RechartsTooltip
          cursor={{ fill: "#fff" }}
          content={<CustomTooltip />}
        />
        <Legend content={<CustomLegend />} />

        <Bar
          dataKey="lowerForecast"
          fill="#aa9bea"
          className="fill-[#aa9bea]"
          stackId="b"
        >
          {commissionsByMonthForGraphAndTooltip.map((_, index) => {
            return (
              <Cell
                key={`cell-${index}`}
                // @ts-expect-error <Type definition is wrong>
                radius={[0, 0, 0, 0]}
              />
            );
          })}
        </Bar>

        <Bar
          dataKey="earned"
          fill="#3A3361"
          className="fill-[#3A3361]"
          stackId="b"
        >
          {commissionsByMonthForGraphAndTooltip.map((entry, index) => {
            return (
              <Cell
                key={`cell-${index}`}
                // @ts-expect-error <Type definition is wrong>
                radius={
                  entry.projectionDifference <= 0 ? [2, 2, 0, 0] : [0, 0, 0, 0]
                }
              />
            );
          })}
        </Bar>

        <Bar
          dataKey="projectionDifference"
          fill="#9F86C0"
          className="fill-[#9F86C0]"
          stackId="b"
        >
          {commissionsByMonthForGraphAndTooltip.map((_, index) => {
            return (
              <Cell
                key={`cell-${index}`}
                // @ts-expect-error <Type definition is wrong>
                radius={[2, 2, 0, 0]}
              />
            );
          })}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
}
